<template>
    <v-card elevation="2" class="ma-1">
        <v-card-text>
            <!-- O que a operação faz -->
            <v-card class="mx-auto mb-4" elevation="4" outlined>
                <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                    O que acontece
                </div>

                <v-card-text class="pt-0 body-2">
                    <v-timeline dense class="pt-0">
                        <v-timeline-item small color="blue-grey" fill-dot>
                            É gerado um <strong>backup do banco de destino</strong>, guardado no servidor
                            da API em <code>storage/app/backups</code>.
                        </v-timeline-item>

                        <v-timeline-item small color="red" fill-dot>
                            As tabelas marcadas são <strong>esvaziadas na loja de destino</strong> —
                            junto com tudo que aponta para elas.
                        </v-timeline-item>

                        <v-timeline-item small color="blue" fill-dot>
                            Os dados da loja de origem são copiados no lugar, mantendo os mesmos ids.
                        </v-timeline-item>
                    </v-timeline>

                    <p class="mb-1">
                        A loja de <strong>origem não é alterada</strong> em nenhum momento — ela só é lida.
                    </p>
                    <p class="mb-0">
                        A cópia roda em uma transação: se der erro no meio, o destino volta ao estado
                        anterior. Enquanto ela roda, o cardápio da loja de destino fica travado.
                    </p>
                </v-card-text>
            </v-card>

            <!-- Origem e destino -->
            <v-row dense>
                <v-col cols="12" md="6">
                    <v-autocomplete
                        v-model="origemId"
                        :items="opcoesEmpresa"
                        :filter="filtrarEmpresa"
                        :disabled="travado"
                        label="Loja de origem (de onde o cardápio vem)"
                        prepend-inner-icon="mdi-content-copy"
                        outlined
                        dense
                        clearable
                    />
                </v-col>

                <v-col cols="12" md="6">
                    <v-autocomplete
                        v-model="destinoId"
                        :items="opcoesEmpresa"
                        :filter="filtrarEmpresa"
                        :disabled="travado"
                        label="Loja de destino (onde os dados serão apagados)"
                        prepend-inner-icon="mdi-alert-octagon"
                        color="red"
                        outlined
                        dense
                        clearable
                    />
                </v-col>
            </v-row>

            <v-alert v-if="mesmaLoja" type="error" dense text class="body-2">
                Origem e destino são a mesma loja.
            </v-alert>

            <!-- Tabelas -->
            <v-card class="mx-auto mb-4" elevation="4" outlined>
                <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                    Tabelas ({{ selecionadas.length }} de {{ catalogo.length }})
                </div>

                <v-card-text class="pt-0">
                    <div class="d-flex align-center mb-2">
                        <span class="caption grey--text">
                            Marcadas por padrão: as mesmas do comando <code>data:copy</code>.
                        </span>
                        <v-spacer/>
                        <v-btn small text color="primary" :disabled="travado" @click="marcarPadrao">
                            Restaurar padrão
                        </v-btn>
                    </div>

                    <v-row dense>
                        <v-col
                            v-for="item in catalogo"
                            :key="item.tabela"
                            cols="12"
                            sm="6"
                            md="4"
                        >
                            <v-checkbox
                                :input-value="selecionadas"
                                :value="item.tabela"
                                :disabled="travado"
                                @change="alternar(item.tabela, $event)"
                                dense
                                hide-details
                                class="mt-0"
                            >
                                <template v-slot:label>
                                    <div>
                                        <div class="body-2">
                                            {{ item.titulo }}
                                            <v-chip v-if="!item.padrao" x-small label class="ml-1">opcional</v-chip>
                                        </div>
                                        <div class="caption grey--text">{{ item.descricao }}</div>
                                        <div class="caption grey--text text--lighten-1">
                                            <code>{{ item.tabela }}</code>
                                        </div>
                                    </div>
                                </template>
                            </v-checkbox>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <!-- Backup -->
            <v-card class="mx-auto mb-4" elevation="4" outlined>
                <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                    Backup do destino
                </div>

                <v-card-text class="pt-0">
                    <v-radio-group v-model="backupEscopo" :disabled="travado" dense hide-details class="mt-0">
                        <v-radio value="tabelas">
                            <template v-slot:label>
                                <div>
                                    <div class="body-2">Só as tabelas afetadas</div>
                                    <div class="caption grey--text">
                                        Leva segundos e é o bastante para desfazer esta clonagem.
                                    </div>
                                </div>
                            </template>
                        </v-radio>

                        <v-radio value="completo" class="mt-2">
                            <template v-slot:label>
                                <div>
                                    <div class="body-2">Banco de destino inteiro</div>
                                    <div class="caption grey--text">
                                        Pega tudo, inclusive o que a clonagem não toca. Numa loja antiga
                                        pode levar minutos e gerar um arquivo grande.
                                    </div>
                                </div>
                            </template>
                        </v-radio>
                    </v-radio-group>
                </v-card-text>
            </v-card>

            <div class="d-flex align-center mb-4">
                <v-btn
                    color="primary"
                    :loading="conferindo"
                    :disabled="!podeConferir || travado"
                    @click="conferir"
                >
                    <v-icon left>mdi-magnify</v-icon>
                    Conferir antes de aplicar
                </v-btn>

                <span v-if="!preflight" class="caption grey--text ml-3">
                    A conferência lê os dois bancos e mostra o que será apagado. Nada é alterado.
                </span>
            </div>

            <!-- Resultado da conferência -->
            <template v-if="preflight">
                <v-alert
                    v-for="texto in preflight.impedimentos"
                    :key="texto"
                    type="error"
                    dense
                    text
                    class="body-2"
                >
                    {{ texto }}
                </v-alert>

                <v-alert v-if="preflight.referencias.length" type="error" dense text class="body-2">
                    <div class="font-weight-bold mb-1">A cópia não terminaria: falta referência no destino.</div>
                    <div v-for="r in preflight.referencias" :key="r.tabela + r.coluna">
                        <strong>{{ r.titulo }}</strong> usa {{ r.total }} registro(s) de
                        <code>{{ r.referencia }}</code> (em <code>{{ r.coluna }}</code>) que o destino não tem —
                        ids {{ r.ids.join(', ') }}{{ r.total > r.ids.length ? '…' : '' }}.
                        Marque <code>{{ r.referencia }}</code> também.
                    </div>
                </v-alert>

                <v-card class="mx-auto mb-4" elevation="4" outlined>
                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                        O que vai acontecer em {{ preflight.destino.fantasia }}
                    </div>

                    <v-card-text class="pt-0">
                        <v-simple-table dense>
                            <thead>
                                <tr>
                                    <th>Tabela</th>
                                    <th class="text-right">Hoje no destino</th>
                                    <th class="text-right">Virá da origem</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="linha in preflight.tabelas" :key="linha.tabela">
                                    <td class="body-2">
                                        {{ linha.titulo }}
                                        <div class="caption grey--text"><code>{{ linha.tabela }}</code></div>
                                    </td>
                                    <td class="text-right">
                                        <span :class="linha.destino ? 'red--text font-weight-bold' : 'grey--text'">
                                            {{ linha.destino === null ? '—' : `${linha.destino} apagadas` }}
                                        </span>
                                    </td>
                                    <td class="text-right">
                                        {{ linha.origem === null ? '—' : linha.origem }}
                                    </td>
                                </tr>
                            </tbody>
                        </v-simple-table>

                        <v-alert v-if="colateraisComDados.length" type="error" dense text class="body-2 mt-3 mb-0">
                            <div class="font-weight-bold mb-1">
                                Estas tabelas também serão esvaziadas, por apontarem para as de cima:
                            </div>
                            <div v-for="c in colateraisComDados" :key="c.tabela">
                                <code>{{ c.tabela }}</code> — {{ c.destino }} registro(s)
                            </div>
                            <div class="mt-1">
                                Elas não são repostas pela cópia. Só o backup traz de volta.
                            </div>
                        </v-alert>

                        <v-alert v-if="colunasPerdidas.length" type="warning" dense text class="body-2 mt-3 mb-0">
                            <div class="font-weight-bold mb-1">
                                Colunas que existem na origem e não no destino — o valor fica para trás:
                            </div>
                            <div v-for="linha in colunasPerdidas" :key="linha.tabela">
                                <code>{{ linha.tabela }}</code>: {{ linha.colunas_nao_copiadas.join(', ') }}
                            </div>
                            <div class="mt-1">
                                O banco do destino está numa migration anterior à do banco da origem.
                            </div>
                        </v-alert>
                    </v-card-text>
                </v-card>

                <v-alert type="error" prominent text class="body-2">
                    <div class="font-weight-bold">Esta ação é irreversível pela tela.</div>
                    O cardápio atual de <strong>{{ preflight.destino.fantasia }}</strong>
                    (<code>{{ preflight.destino.banco }}</code>) é apagado e substituído pelo de
                    <strong>{{ preflight.origem.fantasia }}</strong>. Depois de aplicada, só dá para voltar
                    restaurando o backup no banco, por fora do zaper-control.
                </v-alert>

                <v-checkbox
                    v-model="ciente"
                    :disabled="temImpedimento || travado"
                    dense
                    hide-details
                    class="mt-0 mb-3"
                    color="red"
                    :label="`Entendi que os dados de ${preflight.destino.fantasia} serão apagados.`"
                />

                <v-btn
                    color="red"
                    dark
                    :loading="enviando"
                    :disabled="!ciente || temImpedimento || travado"
                    @click="clonar"
                >
                    <v-icon left>mdi-database-arrow-right</v-icon>
                    Apagar o destino e copiar
                </v-btn>
            </template>

            <!-- Execução -->
            <v-card v-if="clonagem" class="mx-auto mt-4" elevation="4" outlined>
                <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                    Clonagem #{{ clonagem.id }}
                </div>

                <v-card-text class="pt-0">
                    <div class="d-flex align-center mb-2">
                        <v-chip small label dark :color="status(clonagem.status).cor">
                            {{ status(clonagem.status).texto }}
                        </v-chip>
                        <span v-if="clonagem.etapa" class="caption grey--text ml-2">
                            {{ etapa(clonagem.etapa) }}
                        </span>
                        <v-spacer/>
                        <span v-if="clonagem.duracao_ms" class="caption grey--text">
                            {{ duracao(clonagem.duracao_ms) }}
                        </span>
                    </div>

                    <v-progress-linear
                        :value="progresso"
                        :indeterminate="emAndamento && !progresso"
                        height="18"
                        :color="clonagem.status === 'falhou' ? 'red' : 'primary'"
                        class="mb-3"
                    >
                        <span class="caption white--text">{{ copiadas }} / {{ linhas.length }}</span>
                    </v-progress-linear>

                    <v-alert v-if="filaParada" type="warning" dense text class="body-2">
                        Continua na fila há mais de meio minuto. Normalmente começa na hora —
                        vale conferir se o <code>queue:work</code> do servidor da API está rodando.
                    </v-alert>

                    <v-alert v-if="clonagem.erro" type="error" dense text class="body-2">
                        <div class="font-weight-bold mb-1">{{ clonagem.erro }}</div>
                        <span v-if="desfeito">
                            Nada foi alterado no destino: a transação foi desfeita.
                        </span>
                    </v-alert>

                    <v-alert v-if="clonagem.backup_arquivo" type="info" dense text class="body-2">
                        <div class="font-weight-bold mb-1">
                            Backup do destino ({{ bytes(clonagem.backup_bytes) }})
                        </div>
                        <code class="d-block mb-1" style="word-break: break-all;">
                            {{ clonagem.backup_arquivo }}
                        </code>
                        No servidor da API. Para voltar atrás:
                        <code>pg_restore -h ... -U ... -d {{ clonagem.destino_db }} --data-only --disable-triggers "{{ clonagem.backup_arquivo }}"</code>
                    </v-alert>

                    <v-simple-table v-if="linhas.length" dense>
                        <thead>
                            <tr>
                                <th>Tabela</th>
                                <th class="text-right">Apagadas</th>
                                <th class="text-right">Copiadas</th>
                                <th>Situação</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="linha in linhas" :key="linha.tabela">
                                <td class="body-2">{{ linha.titulo || linha.tabela }}</td>
                                <td class="text-right">{{ linha.destino_antes }}</td>
                                <td class="text-right">{{ linha.copiados }}</td>
                                <td>
                                    <v-chip x-small label dark :color="tabela(linha.status).cor">
                                        {{ tabela(linha.status).texto }}
                                    </v-chip>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>

                    <div v-if="!emAndamento" class="mt-3">
                        <v-btn small text color="primary" @click="recomecar">
                            Fazer outra clonagem
                        </v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </v-card-text>
    </v-card>
</template>

<script>
import { status, etapa, tabela, bytes, duracao } from './situacoes';

export default {
    name: 'TabClonagem',

    data: () => ({
        empresas: [],
        catalogo: [],
        origemId: null,
        destinoId: null,
        selecionadas: [],
        backupEscopo: 'tabelas',
        preflight: null,
        ciente: false,
        conferindo: false,
        enviando: false,
        clonagem: null,
        timer: null,
        leituras: 0,
    }),

    async mounted() {
        await this.carregar();
    },

    beforeDestroy() {
        clearInterval(this.timer);
    },

    computed: {
        opcoesEmpresa() {
            return this.empresas.map(e => {
                const nome = e.fantasia || e.razao_social || 'Sem nome';

                return {
                    value: e.id,
                    text: `${e.id} — ${nome}`,
                    // O rótulo mostra id e fantasia, mas quem procura pelo domínio
                    // ou pelo banco continua achando.
                    busca: `${e.id} ${nome} ${e.dominio || ''} emp_${e.database}`.toLowerCase(),
                };
            });
        },

        mesmaLoja() {
            return !!this.origemId && this.origemId === this.destinoId;
        },

        podeConferir() {
            return !!this.origemId && !!this.destinoId && !this.mesmaLoja && this.selecionadas.length > 0;
        },

        temImpedimento() {
            if (!this.preflight) return true;

            return this.preflight.impedimentos.length > 0 || this.preflight.referencias.length > 0;
        },

        colateraisComDados() {
            if (!this.preflight) return [];

            return this.preflight.colaterais.filter(c => c.destino > 0);
        },

        colunasPerdidas() {
            if (!this.preflight) return [];

            return this.preflight.tabelas.filter(
                t => t.colunas_nao_copiadas && t.colunas_nao_copiadas.length
            );
        },

        linhas() {
            return (this.clonagem && this.clonagem.progresso) || [];
        },

        copiadas() {
            return this.linhas.filter(l => l.status === 'concluido').length;
        },

        progresso() {
            if (!this.linhas.length) return 0;
            return Math.round((this.copiadas / this.linhas.length) * 100);
        },

        emAndamento() {
            return !!this.clonagem && ['pendente', 'em_andamento'].includes(this.clonagem.status);
        },

        desfeito() {
            return this.linhas.some(l => l.status === 'desfeito');
        },

        /** Continua `pendente` depois de ~30s: ninguém tirou da fila. */
        filaParada() {
            return !!this.clonagem && this.clonagem.status === 'pendente' && this.leituras > 15;
        },

        /** Enquanto a clonagem roda, mexer nos campos só confundiria. */
        travado() {
            return this.emAndamento;
        },
    },

    watch: {
        origemId: 'invalidar',
        destinoId: 'invalidar',
        selecionadas: 'invalidar',
    },

    methods: {
        status,
        etapa,
        tabela,
        bytes,
        duracao,

        async carregar() {
            this.$root.$emit('loading', true);

            try {
                const [empresas, tabelas] = await Promise.all([
                    this.$http.get('clonagem-cardapio/empresas'),
                    this.$http.get('clonagem-cardapio/tabelas'),
                ]);

                this.empresas = empresas.data.data || [];
                this.catalogo = tabelas.data.data || [];
                this.marcarPadrao();
            } catch (e) {
                this.notify('Não foi possível carregar as lojas e as tabelas', 'error');
            }

            this.$root.$emit('loading', false);
        },

        filtrarEmpresa(item, consulta) {
            return item.busca.includes((consulta || '').toLowerCase());
        },

        marcarPadrao() {
            this.selecionadas = this.catalogo.filter(t => t.padrao).map(t => t.tabela);
        },

        alternar(tabela, marcadas) {
            let ajustadas = marcadas;

            if (marcadas.includes(tabela)) {
                ajustadas = [...marcadas, ...this.dependencias(tabela)];
            } else {
                ajustadas = marcadas.filter(t => !this.dependencias(t).includes(tabela));
            }

            this.selecionadas = [...new Set(ajustadas)];
        },

        dependencias(tabela) {
            const item = this.catalogo.find(t => t.tabela === tabela);

            return (item && item.depende) || [];
        },

        /** Conferência velha com campo novo é conferência errada. */
        invalidar() {
            if (this.travado) return;

            this.preflight = null;
            this.ciente = false;
        },

        async conferir() {
            this.conferindo = true;
            this.preflight = null;
            this.ciente = false;

            try {
                const { data } = await this.$http.post('clonagem-cardapio/preflight', {
                    origem_empresa_id: this.origemId,
                    destino_empresa_id: this.destinoId,
                    tabelas: this.selecionadas,
                });

                if (data.type !== 'success') {
                    this.notify(data.msg || 'Não foi possível conferir', 'warning');
                } else {
                    this.preflight = data.data;
                }
            } catch (e) {
                this.notify('Não foi possível conferir os bancos', 'error');
            }

            this.conferindo = false;
        },

        async clonar() {
            const destino = this.preflight.destino;
            const origem = this.preflight.origem;

            const colateral = this.colateraisComDados.reduce((soma, c) => soma + c.destino, 0);

            const ok = await this.$root.$confirm(
                'Apagar o cardápio do destino',
                `O cardápio de ${destino.fantasia} (${destino.banco}) será apagado e substituído pelo de `
                + `${origem.fantasia}.`
                + (colateral ? ` Mais ${colateral} registro(s) de tabelas ligadas vão junto.` : '')
                + ' Não há como desfazer pela tela.',
                { color: 'red darken-1', width: 460 }
            );

            if (!ok) return;

            this.enviando = true;

            try {
                const { data } = await this.$http.post('clonagem-cardapio', {
                    origem_empresa_id: this.origemId,
                    destino_empresa_id: this.destinoId,
                    tabelas: this.selecionadas,
                    backup_escopo: this.backupEscopo,
                    destino_dominio: destino.dominio,
                });

                if (data.type !== 'success') {
                    this.notify(data.msg || 'Não foi possível iniciar a clonagem', 'warning');
                    this.enviando = false;
                    return;
                }

                this.clonagem = { id: data.data.id, status: 'pendente', progresso: [] };
                this.preflight = null;
                this.ciente = false;
                this.acompanhar();
            } catch (e) {
                this.notify('Não foi possível iniciar a clonagem', 'error');
            }

            this.enviando = false;
        },

        acompanhar() {
            clearInterval(this.timer);
            this.leituras = 0;

            const ler = async () => {
                try {
                    const { data } = await this.$http.get(`clonagem-cardapio/${this.clonagem.id}`);

                    this.leituras++;

                    if (data.type !== 'success') return;

                    this.clonagem = data.data;

                    if (!this.emAndamento) {
                        clearInterval(this.timer);
                        this.$emit('concluido');

                        this.notify(
                            this.clonagem.status === 'concluido'
                                ? 'Clonagem concluída'
                                : 'A clonagem falhou',
                            this.clonagem.status === 'concluido' ? 'success' : 'error'
                        );
                    }
                } catch (e) {
                    clearInterval(this.timer);
                }
            };

            ler();
            this.timer = setInterval(ler, 2000);
        },

        recomecar() {
            clearInterval(this.timer);
            this.clonagem = null;
            this.preflight = null;
            this.ciente = false;
        },
    },
};
</script>
